import {
    ChangeDetectionStrategy,
    Component,
    CUSTOM_ELEMENTS_SCHEMA,
    effect,
    ElementRef,
    inject,
    signal
} from '@angular/core';
import {WjGridModule} from '@mescius/wijmo.angular2.grid';
import {Wijmo} from '../../lib/wjgrid-utils';
import {CollectionView} from '@mescius/wijmo';
import {PortfolioSegmentDataContext} from '../../data/portfolio-segment-data-context.service';
import {PfValidationCheckItem} from '../../data/portfolio-segment-report-data';
import {WjGaugeModule} from '@mescius/wijmo.angular2.gauge';
import {WjChartModule} from '@mescius/wijmo.angular2.chart';
import {AccountSegmentStatisticsCalculator, PortfolioSegmentStatistics} from '../../data/portfolio-segment-statistics';
import {groupBy} from 'es-toolkit';
import {AccountDataWithChecks, CheckEvaluation, CheckSeverity, CheckUtils} from '../../data/app-data-checks';
import {ActivatedRoute, Router} from '@angular/router';
import {WjCharts} from '../../lib/wjcharts';
import {SegmentChecksTour} from './segment-checks.tour';
import {AccountGridComponent, AccountGridFilterCondition} from '../account-grid/account-grid.component';
import {FlexChart, FlexPie} from '@mescius/wijmo.chart';
import {ChildApiService} from '../../data/portfolio-context-resolver.service';

interface PieSeverityCountItem {
    severity: CheckSeverity;
    name: string;
    count: number;
}

/**
 * SegmentChecksComponent is an Angular standalone component responsible for managing and displaying
 * validation checks, statistics, and related graphical data for portfolio segments. It handles data
 * aggregation, updates, and rendering, utilizing Wijmo modules and custom services.
 *
 * Features:
 * - Displays tabular data of validation checks based on aggregated statistics and applied filters.
 * - Renders data visualizations like pie charts and other graphs for detailed statistical insights.
 * - Provides a summary status icon for overall checks based on their severity levels.
 * - Allows interaction via table double-click events, facilitating conditional routing with specific filters.
 * - Integrates a guided tour feature for onboarding or detailing the functionality of the component.
 *
 */
@Component({
    selector: 'app-segment-checks',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    imports: [
        WjGridModule,
        WjGaugeModule,
        WjChartModule
    ],
    templateUrl: './segment-checks.component.html',
    styleUrl: './segment-checks.component.scss'
})
export class SegmentChecksComponent {

    protected readonly Wijmo = Wijmo;

    protected checks = signal<PfValidationCheckItem[]>([]);
    protected statistics = signal<PortfolioSegmentStatistics|undefined>(undefined);

    protected errorsCount = signal<number>(0);
    protected warningsCount = signal<number>(0);

    protected summaryCheckStatusIcon = signal<string>('');

    protected readonly pfSegData = inject(PortfolioSegmentDataContext);
    private readonly childApiService = inject(ChildApiService);

    protected readonly chartData = new CollectionView( [
        { category: 'Expiring', amount: 210_000_000 },
        { category: 'Renewal', amount: 190_000_000 },
    ]);

    protected readonly  pieData: CollectionView<PieSeverityCountItem> = new CollectionView([
        { severity: CheckSeverity.ok, name: 'Valid', count: 518 },
        { severity: CheckSeverity.warning, name: 'Warning', count: 98 },
        { severity: CheckSeverity.error, name: 'Error', count: 52 }
    ]);

    protected readonly ValidationPiePalette = WjCharts.GeneraliValidationPieChartPalette;

    private readonly el = inject<ElementRef<HTMLElement>>(ElementRef);
    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);

    constructor() {
        effect(() => this.prepareChecksTable())
        effect(() => this.refreshStatisticsWithPortfolioData());
    }

    protected statusIcon(severity: CheckSeverity, occurenceCount: number = 0): string {
        switch (occurenceCount === 0 ? CheckSeverity.ok : severity) {
            case CheckSeverity.error: return '<i class="far fa-times-circle check-status-error"></i>';
            case CheckSeverity.warning: return '<i class="far fa-exclamation-triangle check-status-warning"></i>';
            case CheckSeverity.ok: return '<i class="far fa-check-circle check-status-ok"></i>';
            default: return '';
        }
    }

    private prepareChecksTable() {
        const allChecks = this.pfSegData.wb.checksSummary();
        const checksStats = AccountSegmentStatisticsCalculator.aggregateChecks(this.pfSegData.getAccountDataInFilterPeriod());
        const checksCountsById = Object.fromEntries(checksStats.map(it => [it.id, it.count]));

        const filteredChecks = allChecks.map(item => {
            const filteredStats = checksCountsById[item.id];
            return { ...item, count: filteredStats ?? 0 };
        });

        this.checks.set(filteredChecks);
    }

    private pieDataBySeverity(severity: CheckSeverity): PieSeverityCountItem {
        return this.pieData.sourceCollection.find(it => it.severity === severity) as PieSeverityCountItem;
    }

    private refreshStatisticsWithPortfolioData() {
        this.pfSegData.ensureEffectOnFormulaEvaluation();

        const stats = AccountSegmentStatisticsCalculator.fromAccounts(this.pfSegData.getAccountDataInFilterPeriod());
        this.chartData.deferUpdate(() => {
            this.chartData.items[0].amount = stats.totalGnwpEur;
            this.chartData.items[1].amount = stats.renewedGnwpEur;
        });
        this.statistics.set(stats);

        const accounts = this.pfSegData.getAccountDataInFilterPeriod();
        const byCheckStatus = groupBy(accounts, (a: AccountDataWithChecks) => {
            return a.errorLevel ?? CheckSeverity.ok;
        });

        this.pieData.deferUpdate(() => {
            for (const severity in CheckSeverity) {
                const pieData = this.pieDataBySeverity(severity as CheckSeverity);
                pieData.count = byCheckStatus[severity as CheckSeverity]?.length ?? 0;
            }
        });

        const allChecksFlat = accounts.flatMap(a => CheckUtils.getAccountChecks(a));
        const allChecksGrouped = groupBy(allChecksFlat, check => check.severity);

        this.errorsCount.set(allChecksGrouped[CheckSeverity.error]?.length ?? 0);
        this.warningsCount.set(allChecksGrouped[CheckSeverity.warning]?.length ?? 0);

        const summaryCheckStatus = (this.errorsCount() > 0) ? CheckSeverity.error : (this.warningsCount() > 0) ? CheckSeverity.warning : CheckSeverity.ok;
        this.summaryCheckStatusIcon.set(this.statusIcon(summaryCheckStatus));
    }

    async onTrowDblClick(check: PfValidationCheckItem) {
        const filter: AccountGridFilterCondition<string> = { column: 'errorLevel', type: 'equals', value1: check.id }
        await AccountGridComponent.applyFilterAndShow(this.router, this.route, filter);
    }

    protected startTour() {
        SegmentChecksTour.startSegmentTour(this.el.nativeElement);
    }

}
